import { useCallback, useEffect, useState } from 'react';
import { usApi } from '../api';

/**
 * The Popin Watch extension bridge (SPEC_V15 §5.4). Popin Watch keeps two
 * people's Netflix, Prime Video or JioHotstar in step straight between their
 * two browsers: the playback state, the chat beside the player and the call
 * never pass through Popin. This page only introduces them: it hands the
 * extension who the two of you are and a handshake token (POST
 * /v1/us/signal/token), and asks it to start or join a party. It never tells
 * Popin's server what you watch. Over window.postMessage:
 *
 *   ext → page  {source:'popin-watch-ext', type:'hello', version, target:'chrome'|'safari',
 *                linked: {user_id, expires_at} | null}
 *   page → ext  {source:'popin-watch-page', type:'hello'}
 *               {…, type:'link', link: SignalTokenView} — who you two are, the handshake token
 *               {…, type:'unlink'}
 *               {…, type:'party', url} — open the title with the sidebar; ring the partner
 *               {…, type:'join'} — join the partner's party (a tab that becomes their title)
 */
const EXT = 'popin-watch-ext';
const PAGE = 'popin-watch-page';

export interface ExtLinked {
  user_id: string;
  expires_at: string;
}

export interface WatchExtension {
  /** Set once the extension said hello. */
  version: string | null;
  target: 'chrome' | 'safari' | null;
  linked: ExtLinked | null;
  /** Start a party on this streaming link (nothing goes to Popin's server). */
  startParty: (url: string) => void;
  join: () => void;
}

const post = (m: Record<string, unknown>) => window.postMessage({ source: PAGE, ...m }, window.location.origin);

/** Only well-formed hellos from the extension (anything on the page can post). */
export function parseHello(d: Record<string, unknown>): { version: string; target: 'chrome' | 'safari'; linked: ExtLinked | null } | null {
  if (d.type !== 'hello' || typeof d.version !== 'string' || !/^\d+(\.\d+){0,3}$/.test(d.version)) return null;
  const l = d.linked as Record<string, unknown> | null | undefined;
  const linked = l && typeof l.user_id === 'string' && typeof l.expires_at === 'string' ? { user_id: l.user_id, expires_at: l.expires_at } : null;
  return { version: d.version, target: d.target === 'safari' ? 'safari' : 'chrome', linked };
}

/** Hand the extension a new token when it has none, someone else's, or one about to run out. */
export function needsLink(linked: ExtLinked | null, meId: string | undefined, now = Date.now()): boolean {
  if (!meId) return false;
  if (!linked || linked.user_id !== meId) return true;
  return Date.parse(linked.expires_at) - now < 2 * 24 * 3600 * 1000;
}

export function useWatchExtension(): WatchExtension {
  const [hello, setHello] = useState<ReturnType<typeof parseHello>>(null);
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.source !== window || e.origin !== window.location.origin) return;
      const d = e.data as Record<string, unknown> | null;
      if (!d || d.source !== EXT) return;
      const h = parseHello(d);
      if (h) setHello(h);
    };
    window.addEventListener('message', onMsg);
    post({ type: 'hello' });
    return () => window.removeEventListener('message', onMsg);
  }, []);
  const startParty = useCallback((url: string) => post({ type: 'party', url }), []);
  const join = useCallback(() => post({ type: 'join' }), []);
  return { version: hello?.version ?? null, target: hello?.target ?? null, linked: hello?.linked ?? null, startParty, join };
}

/**
 * Keep the extension linked to this person's space (run inside Us): a fresh
 * handshake token whenever it has none or an old one. The token only opens
 * the handshake relay for this space; it is not a session.
 */
export function useLinkWatchExtension(meId: string | undefined, hasSpace: boolean): WatchExtension {
  const ext = useWatchExtension();
  const stale = !!ext.version && hasSpace && needsLink(ext.linked, meId);
  useEffect(() => {
    if (!stale) return;
    let off = false;
    usApi
      .signalToken()
      .then((link) => !off && post({ type: 'link', link }))
      .catch(() => undefined); // not set up here, or no space: count-in mode
    return () => {
      off = true;
    };
  }, [stale]);
  return ext;
}

/** A short, soft beep for count-ins and "pause yours" prompts. */
export function beep(freq = 880, ms = 140): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + ms / 1000);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + ms / 1000 + 0.05);
    o.onended = () => void ctx.close();
  } catch {
    /* no audio: the countdown is on screen anyway */
  }
}
